import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Users } from 'lucide-react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface ResponsibleTeamData {
  adminEmail: string;
  adminName: string;
  adminPhone: string;
  backupAdminEmail: string;
  backupAdminName: string;
  department: string;
}

interface IResponsibleTeam {
  onSave?: (data: ResponsibleTeamData) => Promise<void> | void;
}

export const ResponsibleTeam: React.FC<IResponsibleTeam> = ({ onSave }) => {
  const initialData: ResponsibleTeamData = {
    adminEmail: 'admin@acmecorp.com',
    adminName: 'John Smith',
    adminPhone: '+1 (555) 123-4567',
    backupAdminEmail: 'backup@acmecorp.com',
    backupAdminName: 'Jane Doe',
    department: 'IT Operations',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  return (
    <Box sx={{ gridColumn: { md: 'span 2' } }}>
      <SettingsCard
        title="Responsible Team"
        description="Manage administrative contacts and responsible personnel"
        icon={<Users size={20} />}
        isEditing={isEditing}
        isSaving={isSaving}
        onEditToggle={handleEditToggle}
        onSave={handleSave}
      >
        {isEditing ? (
          <Stack spacing={3}>
            {/* Primary Administrator */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Primary Administrator
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                <FloatingLabelInput
                  label="Admin Name"
                  value={formData.adminName}
                  onChange={(e) => handleInputChange('adminName', e.target.value)}
                />
                <FloatingLabelInput
                  label="Admin Email"
                  value={formData.adminEmail}
                  onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                />
                <FloatingLabelInput
                  label="Admin Phone"
                  value={formData.adminPhone}
                  onChange={(e) => handleInputChange('adminPhone', e.target.value)}
                />
              </Box>
            </Box>

            {/* Backup Administrator */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Backup Administrator
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                <FloatingLabelInput
                  label="Backup Admin Name"
                  value={formData.backupAdminName}
                  onChange={(e) => handleInputChange('backupAdminName', e.target.value)}
                />
                <FloatingLabelInput
                  label="Backup Admin Email"
                  value={formData.backupAdminEmail}
                  onChange={(e) => handleInputChange('backupAdminEmail', e.target.value)}
                />
              </Box>
            </Box>

            {/* Department */}
            <Box sx={{ maxWidth: { xs: '100%', sm: 400 } }}>
              <FloatingLabelInput
                label="Department"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
              />
            </Box>
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* Primary Administrator */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Primary Administrator
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                  <DetailedItem label="Admin Name" text={formData.adminName} />
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                  <DetailedItem label="Admin Email" text={formData.adminEmail} />
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                  <DetailedItem label="Admin Phone" text={formData.adminPhone} />
                </Paper>
              </Box>
            </Box>

            {/* Backup Administrator */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Backup Administrator
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                  <DetailedItem label="Backup Admin Name" text={formData.backupAdminName} />
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                  <DetailedItem label="Backup Admin Email" text={formData.backupAdminEmail} />
                </Paper>
              </Box>
            </Box>

            {/* Department */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: '8px',
                bgcolor: 'rgba(59, 130, 246, 0.04)',
                borderColor: 'rgba(59, 130, 246, 0.2)',
              }}
            >
              <DetailedItem
                label="Department"
                text={formData.department}
                helpertext="The department responsible for managing this organization's settings."
              />
            </Paper>
          </Stack>
        )}
      </SettingsCard>
    </Box>
  );
};
